import React, { useState } from 'react';
import CustomSelect from '../../Common/CustomSelect';

const ItemInventoryTab = ({
  allocations = [],
  loading = false,
  warehouses = [],
  racks = [],
  pagination = { page: 1, limit: 20, total: 0, pages: 1 },
  onPageChange,
  storeFilter,
  setStoreFilter,
  rackFilter,
  setRackFilter,
  levelFilter,
  setLevelFilter,
  stockStatusFilter,
  setStockStatusFilter,
  onSelectItem,
  onDeallocate
}) => {
  const [deallocModalData, setDeallocModalData] = useState(null);
  const [reduceQty, setReduceQty] = useState('');
  const [reduceNote, setReduceNote] = useState('');
  const [deallocLoading, setDeallocLoading] = useState(false);

  const handleDeallocSubmit = async (e) => {
    e.preventDefault();
    if (!deallocModalData) return;
    try {
      setDeallocLoading(true);
      await onDeallocate(deallocModalData._id, {
        quantityToReduce: reduceQty ? parseFloat(reduceQty) : undefined,
        note: reduceNote
      });
      setDeallocModalData(null);
      setReduceQty('');
      setReduceNote('');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to deallocate item');
    } finally {
      setDeallocLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <CustomSelect
            label="Filter by Store/Warehouse"
            value={storeFilter}
            onChange={(e) => setStoreFilter(e.target.value)}
            placeholder="All Stores / Warehouses"
            options={[
              { value: '', label: 'All Stores / Warehouses' },
              ...warehouses.map(w => ({
                value: w._id,
                label: `${w.name} (${w.code || 'Store'})`
              }))
            ]}
          />
        </div>

        <div>
          <CustomSelect
            label="Filter by Rack"
            value={rackFilter}
            onChange={(e) => setRackFilter(e.target.value)}
            placeholder="All Racks"
            options={[
              { value: '', label: 'All Racks' },
              ...racks.map(r => ({
                value: r._id,
                label: `Rack ${r.rackNumber} (${r.name || 'General'})`
              }))
            ]}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-800 mb-1">
            Filter by Level
          </label>
          <input
            type="text"
            placeholder="e.g. 1, 2, A, B..."
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all"
          />
        </div>

        <div>
          <CustomSelect
            label="Stock Status"
            value={stockStatusFilter}
            onChange={(e) => setStockStatusFilter(e.target.value)}
            placeholder="All Stock Status"
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'in_stock', label: 'In Stock (> 0)' },
              { value: 'low_stock', label: 'Low Stock (< Reorder)' }
            ]}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-20 text-center text-gray-500">
            <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm">Loading storage records...</p>
          </div>
        ) : allocations.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h4 className="text-sm font-bold text-gray-900">No Item Allocations Found</h4>
            <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
              No stock is stored under the selected filters. Use &quot;Add Item&quot; to assign warehouse stock to rack positions.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50 text-gray-600 font-bold uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Item Name &amp; Code</th>
                  <th className="px-4 py-3 text-left">Store / Warehouse</th>
                  <th className="px-4 py-3 text-left">Rack</th>
                  <th className="px-4 py-3 text-center">Level</th>
                  <th className="px-4 py-3 text-center">Position</th>
                  <th className="px-4 py-3 text-right">Quantity at Pos</th>
                  <th className="px-4 py-3 text-center">Unit</th>
                  <th className="px-4 py-3 text-center">Stock Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {allocations.map(alloc => {
                  const item = alloc.item || {};
                  const storeObj = warehouses.find(w => w._id === alloc.store);
                  const isLow = (alloc.quantity || 0) <= (item.inventory?.reorderPoint || 0);

                  return (
                    <tr
                      key={alloc._id}
                      className="hover:bg-indigo-50/30 transition-colors group cursor-pointer"
                      onClick={() => onSelectItem(item._id || alloc.item)}
                    >
                      {/* Item Details */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 font-extrabold flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                          </div>
                          <div>
                            <div className="font-extrabold text-gray-900 hover:text-indigo-600 transition-colors">
                              {item.name || 'Item Name'}
                            </div>
                            <div className="text-[11px] text-gray-500 font-mono">
                              SKU: {item.sku || '—'} {item.barcode && `| Barcode: ${item.barcode}`}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Store */}
                      <td className="px-4 py-3 text-gray-800 font-medium">
                        {storeObj ? storeObj.name : 'Store'}
                      </td>

                      {/* Rack */}
                      <td className="px-4 py-3">
                        <span className="font-extrabold text-indigo-700">
                          {alloc.rackNumber}
                        </span>
                        {alloc.rack?.zone && (
                          <span className="text-[10px] text-gray-400 block">Zone: {alloc.rack.zone}</span>
                        )}
                      </td>

                      {/* Level */}
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 font-bold">
                          L{alloc.levelNumber}
                        </span>
                      </td>

                      {/* Position */}
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-extrabold">
                          {alloc.positionNumber}
                        </span>
                      </td>

                      {/* Quantity */}
                      <td className="px-4 py-3 text-right font-black text-gray-900 text-sm">
                        {alloc.quantity}
                      </td>

                      {/* Unit */}
                      <td className="px-4 py-3 text-center text-gray-500 font-medium">
                        {alloc.unit || 'PCS'}
                      </td>

                      {/* Stock Status */}
                      <td className="px-4 py-3 text-center">
                        {alloc.quantity <= 0 ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
                            Empty
                          </span>
                        ) : isLow ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
                            Low Stock
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">
                            In Stock
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => onSelectItem(item._id || alloc.item)}
                            className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="View Full Item Details"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setDeallocModalData(alloc);
                              setReduceQty(alloc.quantity.toString());
                            }}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Deallocate / Remove Stock"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination bar */}
        {pagination.pages > 1 && (
          <div className="p-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-600">
            <div>
              Showing Page <span className="font-bold">{pagination.page}</span> of <span className="font-bold">{pagination.pages}</span> ({pagination.total} total allocations)
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => onPageChange(pagination.page - 1)}
                className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={pagination.page >= pagination.pages}
                onClick={() => onPageChange(pagination.page + 1)}
                className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Deallocation Modal */}
      {deallocModalData && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/40 backdrop-blur-sm flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 shadow-2xl border border-gray-200 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">
                Deallocate Item Stock
              </h3>
              <button
                onClick={() => setDeallocModalData(null)}
                className="text-gray-400 hover:text-gray-600 text-lg font-bold"
              >
                &times;
              </button>
            </div>

            <div className="p-3 bg-gray-50 rounded-xl text-xs space-y-1">
              <div>Item: <strong className="text-gray-900">{deallocModalData.item?.name}</strong></div>
              <div>Position: <strong className="text-indigo-700">Rack {deallocModalData.rackNumber} → Level {deallocModalData.levelNumber} → Pos {deallocModalData.positionNumber}</strong></div>
              <div>Current Quantity: <strong className="text-gray-900">{deallocModalData.quantity} {deallocModalData.unit}</strong></div>
            </div>

            <form onSubmit={handleDeallocSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Quantity to Deallocate / Return to Bulk
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="any"
                  max={deallocModalData.quantity}
                  value={reduceQty}
                  onChange={(e) => setReduceQty(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 font-bold"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Reason / Notes (Audit Record)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Moved to dispatch, damaged, physical audit adjustment"
                  value={reduceNote}
                  onChange={(e) => setReduceNote(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setDeallocModalData(null)}
                  disabled={deallocLoading}
                  className="px-3 py-1.5 text-xs text-gray-700 bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={deallocLoading}
                  className="px-4 py-1.5 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm disabled:opacity-50"
                >
                  {deallocLoading ? 'Processing...' : 'Confirm Deallocation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemInventoryTab;
